import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef } from 'react';
import { Icon, type LucideIcon } from '../icons/Icon';

type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand';
type BadgeSize = 'sm' | 'md';

const toneStyle: Record<StatusTone, HTMLChakraProps<'span'>> = {
  success: { bg: 'status.successSubtle', color: 'status.successFg' },
  warning: { bg: 'status.warningSubtle', color: 'status.warningFg' },
  danger: { bg: 'status.dangerSubtle', color: 'status.dangerFg' },
  info: { bg: 'status.infoSubtle', color: 'status.infoFg' },
  neutral: { bg: 'surface.subtle', color: 'text.secondary' },
  brand: { bg: 'brand.subtle', color: 'brand.fg' },
};

const sizeStyle: Record<BadgeSize, HTMLChakraProps<'span'>> = {
  // `sm` renders at 11px (`2xs`). WCAG has no large-text relaxation here, so every tone's
  // fg-on-subtle-bg must clear 4.5:1 even at this size — including the danger/high-risk pill
  // (red text on its pink-tinted subtle bg). Guarded in theme/__tests__/contrast.test.ts.
  sm: { fontSize: '2xs', px: '1.5', py: '0.5', gap: '1' },
  md: { fontSize: 'xs', px: '2', py: '1', gap: '1.5' },
};

const Base = chakra('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: 'body',
    fontWeight: 'semibold',
    lineHeight: 'none',
    borderRadius: 'full',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
});

export interface StatusBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  tone?: StatusTone;
  label: string;
  /** Optional leading glyph (a lucide icon). */
  icon?: LucideIcon;
  size?: BadgeSize;
  /** Show a filled status dot instead of an icon. */
  dot?: boolean;
}

/** A compact, semantic status pill (tone + label, optional icon or dot). */
export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { tone = 'neutral', label, icon, size = 'md', dot, ...rest },
  ref,
) {
  return (
    <Base ref={ref} {...toneStyle[tone]} {...sizeStyle[size]} {...rest}>
      {dot && (
        <chakra.span
          aria-hidden
          boxSize="1.5"
          borderRadius="full"
          bg="currentColor"
          flexShrink={0}
        />
      )}
      {!dot && icon && <Icon as={icon} size={size === 'sm' ? 'xs' : 'sm'} tone="current" />}
      {label}
    </Base>
  );
});
